import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Form, ProgressBar, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { customStyles } from './MultiSelectStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileUpload, 
  faArrowLeft, 
  faSpinner,
  faCheck, 
  faPlus,
  faTimes,
  faTags,
  faFilePdf,
  faFileWord,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { documentsAPI, projectsAPI } from '../../services/api';

const UploadDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdParam = queryParams.get('project_id');
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Policy tags state
  const [policyTags, setPolicyTags] = useState([
    'Privacy Policy',
    'Terms of Service',
    'Security Policy',
    'Acceptable Use Policy',
    'Cookie Policy',
    'Data Retention Policy',
    'Incident Response Policy',
    'Business Continuity Plan',
    'Employee Handbook',
    'Code of Conduct',
    'Environmental Policy',
    'Health and Safety Policy',
    'Social Media Policy',
    'Remote Work Policy',
    'Anti-Discrimination Policy',
    'Whistleblower Policy',
    'Conflict of Interest Policy',
    'Information Security Policy'
  ]);
  
  // Frameworks list for compliance analysis
  const [frameworks, setFrameworks] = useState([
    'CIS Critical Security Controls',
    'COBIT (Control Objectives for IT)',
    'Companies Act 2013',
    'CSA Cloud Controls Matrix (CCM)',
    'EU AI Act',
    'GDPR (EU General Data Protection Regulation)',
    'HIPAA / HITECH',
    'HITRUST CSF',
    'IEC 27032',
    'India DPDP Act 2023',
    'IRDAI Risk & Governance Guidelines',
    'ISO 13485',
    'ISO 19600',
    'ISO 37301',
    'ISO/IEC 20000',
    'ISO/IEC 22301',
    'ISO/IEC 27001',
    'ISO/IEC 27017',
    'ISO/IEC 27018',
    'ISO/IEC 27701',
    'ISO/IEC 31000',
    'NIST Cybersecurity Framework (CSF)',
    'NIST Privacy Framework',
    'Payment Card Industry DSS (PCI DSS)',
    'RBI Cybersecurity & BCP Master Directions',
    'RBI Prudential Regulations (PCA, Basel)',
    'Sarbanes-Oxley Act (SOX)',
    'SEBI LODR (Listing Obligations & Disclosure Requirements)',
    'SOC 1 and SOC 2 (Trust Services Criteria)',
    'TISAX (Trusted Information Security Assessment Exchange)'
  ]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [customTags, setCustomTags] = useState([]);
  
  // API URL is handled by the API service

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await projectsAPI.getProjects();
      
      if (response.data.status === 200) {
        setProjects(response.data.data.projects || []);
      } else {
        setError('Failed to load projects');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle tag selection
  const handleTagSelection = (selectedOptions) => {
    const selectedValues = selectedOptions.map(option => option.value);
    setSelectedTags(selectedValues);
  };
  
  // Handle framework selection
  const handleFrameworkSelection = (selectedOptions) => {
    const selectedValues = selectedOptions.map(option => option.value);
    setSelectedFrameworks(selectedValues);
  };
  
  // Handle custom tag addition
  const handleAddCustomTag = () => {
    if (customTagInput.trim() !== '' && !customTags.includes(customTagInput.trim())) {
      setCustomTags([...customTags, customTagInput.trim()]);
      setCustomTagInput('');
    }
  };
  
  // Handle custom tag removal
  const handleRemoveCustomTag = (tag) => {
    setCustomTags(customTags.filter(t => t !== tag));
  };
  
  // Get all selected tags (predefined + custom)
  const getAllTags = () => {
    return [...selectedTags, ...customTags];
  };
  
  // Handle project selection
  const handleProjectSelection = (selectedOption) => {
    return selectedOption ? selectedOption.value : "";
  };
  
  // Get all selected frameworks
  const getAllFrameworks = () => {
    return selectedFrameworks;
  };
  
  // Validation schema
  const validationSchema = Yup.object({
    project_id: Yup.number()
      .required('Project is required'),
    document_files: Yup.array()
      .min(1, 'At least one document file is required')
      .max(5, 'Maximum 5 files can be uploaded at once')
      .test('fileFormat', 'Unsupported file format. Please upload PDF, DOC, DOCX, or TXT files only.', (values) => {
        if (!values || values.length === 0) return true; // Skip validation if no files
        const supportedFormats = ['pdf', 'doc', 'docx', 'txt'];
        return values.every(file => {
          const extension = file.name.split('.').pop().toLowerCase();
          return supportedFormats.includes(extension);
        });
      })
      .test('fileSize', 'File size must be less than 10MB per file', (values) => {
        if (!values || values.length === 0) return true; // Skip validation if no files
        return values.every(file => file.size <= 10 * 1024 * 1024); // 10MB in bytes
      })
  });

  // Initial form values
  const initialValues = {
    project_id: projectIdParam || '',
    document_files: []
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setError('');
    
    // Check if at least one tag is selected
    const allTags = getAllTags();
    if (allTags.length === 0) {
      setError('Please select at least one policy tag');
      setSubmitting(false);
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    // Track total uploads and successful uploads
    const totalFiles = values.document_files.length;
    let successfulUploads = 0;
    let failedUploads = 0;
    const uploadedDocumentIds = [];
    
    try {
      // Upload each file sequentially
      for (let i = 0; i < values.document_files.length; i++) {
        const file = values.document_files[i];
        
        // Create form data for file upload
        const formData = new FormData();
        formData.append('project_id', values.project_id);
        formData.append('file', file); // Changed from 'document_file' to 'file' to match backend
        
        // Append all tags to the form data
        allTags.forEach(tag => {
          formData.append('tags[]', tag);
        });
        
        // Append all selected frameworks to the form data
        const allFrameworks = getAllFrameworks();
        allFrameworks.forEach(framework => {
          formData.append('frameworks[]', framework);
        });
        
        // Update progress to show which file is being uploaded
        setUploadProgress(Math.round((i / totalFiles) * 100));
        
        try {
          // Using the documentsAPI service for upload
          const response = await documentsAPI.uploadDocument(formData, {
            onUploadProgress: (progressEvent) => {
              // Calculate overall progress: completed files + current file progress
              const fileProgress = progressEvent.loaded / progressEvent.total;
              const overallProgress = Math.round(((i + fileProgress) / totalFiles) * 100);
              setUploadProgress(overallProgress);
            }
          });
          
          if (response.data.status === 201) {
            // Upload successful
            successfulUploads++;
            uploadedDocumentIds.push(response.data.data.document_id);
          } else {
            failedUploads++;
            console.error(`Failed to upload ${file.name}: ${response.data.message}`);
          }
        } catch (err) {
          failedUploads++;
          console.error(`Error uploading ${file.name}:`, err);
        }
      }
      
      // All uploads completed
      if (successfulUploads === totalFiles) {
        // All uploads successful
        setTimeout(() => {
          // If only one document was uploaded, navigate to that document
          // Otherwise navigate to the project page
          if (uploadedDocumentIds.length === 1) {
            navigate(`/documents/${uploadedDocumentIds[0]}`);
          } else {
            navigate(`/projects/${values.project_id}`);
          }
        }, 1000);
      } else if (successfulUploads > 0) {
        // Some uploads successful
        setError(`${successfulUploads} of ${totalFiles} documents uploaded successfully. ${failedUploads} failed.`);
        setTimeout(() => {
          navigate(`/projects/${values.project_id}`);
        }, 2000);
      } else {
        // All uploads failed
        setError('Failed to upload documents. Please try again.');
        setIsUploading(false);
      }
    } catch (err) {
      console.error('Error in upload process:', err);
      
      if (err.response) {
        setError(err.response.data.message || 'Failed to upload documents');
      } else {
        setError('Network error. Please try again later.');
      }
      
      setIsUploading(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading...</h3>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Upload Document</h1>
          
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ 
                  handleSubmit, 
                  handleChange, 
                  handleBlur, 
                  values, 
                  touched, 
                  errors, 
                  isSubmitting,
                  setFieldValue 
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project *</Form.Label>
                      <Select
                        name="project_id"
                        options={projects.map(project => ({ value: project.id, label: project.name }))}
                        value={values.project_id ? { value: values.project_id, label: projects.find(p => p.id === values.project_id)?.name || '' } : null}
                        onChange={(selectedOption) => {
                          setFieldValue("project_id", handleProjectSelection(selectedOption));
                        }}
                        onBlur={() => handleBlur({ target: { name: 'project_id' } })}
                        isDisabled={isUploading}
                        placeholder="Select a project"
                        styles={customStyles}
                        className={touched.project_id && errors.project_id ? 'is-invalid' : ''}
                        classNamePrefix="select"
                      />
                      <div className="invalid-feedback" style={{ display: touched.project_id && errors.project_id ? 'block' : 'none' }}>
                        {errors.project_id}
                      </div>
                    </Form.Group>
                    
                    {/* Document Title field removed */}
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Document Files</Form.Label>
                      <div className="custom-file-upload">
                        <div className="file-upload-wrapper border rounded p-3" style={{ 
                          backgroundColor: '#f8f9fa', 
                          borderStyle: 'dashed', 
                          borderColor: touched.document_files && !!errors.document_files ? '#dc3545' : '#ced4da',
                          cursor: isUploading ? 'not-allowed' : 'pointer',
                          opacity: isUploading ? 0.7 : 1,
                          position: 'relative' // Added position relative to contain absolute positioned input
                        }}>
                          <div className="text-center py-3">
                            <FontAwesomeIcon 
                              icon={faFileUpload} 
                              size="2x" 
                              className="mb-2 text-primary" 
                            />
                            <p className="mb-1">
                              {values.document_files && values.document_files.length > 0 
                                ? `${values.document_files.length} file(s) selected` 
                                : 'Click or drag files to upload'}
                            </p>
                            <p className="small text-muted mb-0">
                              Supported formats: PDF, DOCX, DOC, TXT<br/>
                              Max size: 10MB per file | Limit: 5 files maximum
                            </p>
                          </div>
                          <Form.Control
                            type="file"
                            name="document_files"
                            multiple
                            onChange={(event) => {
                              const files = Array.from(event.currentTarget.files);
                              setFieldValue("document_files", files);
                            }}
                            onBlur={handleBlur}
                            disabled={isUploading}
                            style={{
                              opacity: 0,
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              cursor: isUploading ? 'not-allowed' : 'pointer'
                            }}
                          />
                        </div>
                      </div>
                      <Form.Control.Feedback type="invalid" style={{ display: touched.document_files && !!errors.document_files ? 'block' : 'none' }}>
                        {typeof errors.document_files === 'string' 
                          ? errors.document_files 
                          : errors.document_files && typeof errors.document_files === 'object' 
                            ? Object.values(errors.document_files).join(', ') 
                            : 'Invalid file selection'}
                      </Form.Control.Feedback>
                      <div className="mt-2 d-flex justify-content-center">
                        <Badge bg="info" className="me-2">Up to 5 files at once</Badge>
                        <Badge bg="secondary">Hold Ctrl/Cmd to select multiple files</Badge>
                      </div>
                    </Form.Group>
                    
                    {/* Selected Files Preview */}
                    {values.document_files && values.document_files.length > 0 && (
                      <div className="mb-3 mt-3">
                        <h6>Selected Files ({values.document_files.length})</h6>
                        <div className="selected-files-list">
                          {values.document_files.map((file, index) => (
                            <div key={index} className="selected-file-item p-2 mb-1 border rounded d-flex justify-content-between align-items-center">
                              <div>
                                <FontAwesomeIcon 
                                  icon={file.type.includes('pdf') ? faFilePdf : file.type.includes('word') ? faFileWord : faFileAlt} 
                                  className={`me-2 ${file.type.includes('pdf') ? 'text-danger' : file.type.includes('word') ? 'text-primary' : 'text-secondary'}`}
                                />
                                <span className="text-truncate" style={{maxWidth: '200px', display: 'inline-block'}}>{file.name}</span>
                                <Badge bg="secondary" className="ms-2">{(file.size / 1024 / 1024).toFixed(2)} MB</Badge>
                              </div>
                              <Button 
                                variant="link" 
                                className="text-danger p-0" 
                                onClick={() => {
                                  const newFiles = [...values.document_files];
                                  newFiles.splice(index, 1);
                                  setFieldValue("document_files", newFiles);
                                }}
                                disabled={isUploading}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Policy Tags Section */}
                    <Form.Group className="mb-4 mt-4">
                      <Form.Label>
                        <FontAwesomeIcon icon={faTags} className="me-2" />
                        Select Policy Tags
                      </Form.Label>
                      <Select
                        isMulti
                        name="policy-tags"
                        options={policyTags.map(tag => ({ value: tag, label: tag }))}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select policy tags..."
                        onChange={handleTagSelection}
                        isDisabled={isUploading}
                        value={selectedTags.map(tag => ({ value: tag, label: tag }))}
                        styles={customStyles}
                      />
                      <Form.Text className="text-muted">
                        You can select multiple tags
                      </Form.Text>
                    </Form.Group>
                    
                    {/* Compliance Frameworks Section */}
                    <Form.Group className="mb-4 mt-4">
                      <Form.Label>
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        Select Compliance Frameworks for Analysis
                      </Form.Label>
                      <Select
                        isMulti
                        name="compliance-frameworks"
                        options={frameworks.map(framework => ({ value: framework, label: framework }))}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select compliance frameworks..."
                        onChange={handleFrameworkSelection}
                        isDisabled={isUploading}
                        value={selectedFrameworks.map(framework => ({ value: framework, label: framework }))}
                        styles={customStyles}
                      />
                      <Form.Text className="text-muted">
                        You can select multiple frameworks
                        {selectedFrameworks.length > 0 && (
                          <span className="ms-2">
                            Selected frameworks: {selectedFrameworks.length}
                          </span>
                        )}
                      </Form.Text>
                    </Form.Group>
                    
                    {/* Custom Tags Section */}
                    <Form.Group className="mb-4">
                      <Form.Label>Add Custom Tag (Optional)</Form.Label>
                      <div className="input-group">
                        <Form.Control
                          type="text"
                          placeholder="Enter custom tag"
                          value={customTagInput}
                          onChange={(e) => setCustomTagInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomTag();
                            }
                          }}
                          disabled={isUploading}
                        />
                        <Button 
                          variant="outline-primary" 
                          onClick={handleAddCustomTag}
                          disabled={!customTagInput.trim() || isUploading}
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add Tag
                        </Button>
                      </div>
                    </Form.Group>
                    
                    {/* Custom Tags Display */}
                    {customTags.length > 0 && (
                      <div className="mb-4">
                        <Form.Label>Custom Tags:</Form.Label>
                        <div id="custom_tags_container">
                          {customTags.map((tag, index) => (
                            <Badge 
                              bg="primary" 
                              className="me-2 mb-2 p-2" 
                              key={index}
                            >
                              {tag}
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="p-0 ms-2 text-light" 
                                onClick={() => handleRemoveCustomTag(tag)}
                                disabled={isUploading}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {isUploading && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Upload Progress</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <ProgressBar 
                          now={uploadProgress} 
                          variant="primary" 
                          animated 
                          striped
                        />
                        <div className="text-center mt-2">
                          {uploadProgress === 100 ? (
                            <div className="text-success">
                              <FontAwesomeIcon icon={faCheck} className="me-2" />
                              Upload complete! Redirecting...
                            </div>
                          ) : (
                            <div>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Uploading file {Math.min(Math.ceil(uploadProgress * values.document_files.length / 100), values.document_files.length)} of {values.document_files.length}...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="secondary" 
                        onClick={() => navigate(-1)}
                        disabled={isUploading}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back
                      </Button>
                      
                      <Button 
                        type="submit" 
                        variant="primary"
                        disabled={isSubmitting || isUploading}
                      >
                        {isSubmitting || isUploading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                            Upload Document
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          {/* Selected Tags Card */}
          {getAllTags().length > 0 && (
            <Card className="shadow-sm mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faTags} className="me-2" />
                  Selected Tags
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-0">
                  {selectedTags.map((tag, index) => (
                    <Badge 
                      bg="primary" 
                      className="me-2 mb-2 p-2" 
                      key={`selected-${index}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                  {customTags.map((tag, index) => (
                    <Badge 
                      bg="info" 
                      className="me-2 mb-2 p-2" 
                      key={`custom-${index}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
          
          {/* Upload Guidelines Card */}
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Upload Guidelines</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <h6>Supported File Types</h6>
                <ul className="mb-0">
                  <li>PDF (.pdf)</li>
                  <li>Microsoft Word (.docx, .doc)</li>
                  <li>Text (.txt)</li>
                </ul>
              </div>
              
              <div className="mb-3">
                <h6>File Size Limits</h6>
                <p className="mb-0">Maximum file size: 10MB per file</p>
                <p className="mb-0">Maximum 5 files can be uploaded at once</p>
              </div>
              
              
              
              {/* <Alert variant="warning" className="mb-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                Please ensure your document does not contain sensitive personal information unless necessary for the review.
              </Alert> */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UploadDocument;
